import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { CheckCircle2, Loader2, Sparkles, Zap, Crown, Building2, Landmark } from 'lucide-react';
import axios from 'axios';
import { useLocalPrice } from '../lib/localCurrency';
import { formatCurrency } from '../lib/currency';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const planIcons = { basic: Zap, pro: Sparkles, enterprise: Crown };
const iconForSlug = (slug) => {
  if (planIcons[slug]) return planIcons[slug];
  if (slug?.endsWith('-partner')) return Zap;
  if (slug?.endsWith('-premier')) return Crown;
  if (slug?.endsWith('-starter')) return Sparkles;
  return Building2;
};

// Roles with their own dedicated plan set (see backend subscription_plans "role" field).
// Any other role (employer, recruiter, job_seeker, etc.) gets the default employer plans.
const ROLE_PLAN_PAGES = {
  course_provider: { role: 'course_provider', title: 'Course Provider Pricing', subtitle: "Choose the plan that's right for listing and promoting your courses." },
  event_host: { role: 'event_host', title: 'Event Host Pricing', subtitle: "Choose the plan that's right for hosting and promoting your events." },
  university_manager: { role: 'university_manager', title: 'University Partner Pricing', subtitle: 'Annual plans for universities and training providers listing courses and programmes.' },
};

export default function PricingPage() {
  const navigate = useNavigate();
  const { token, isAuthenticated, user, loading: authLoading } = useAuth();
  const localPrice = useLocalPrice(user?.country);
  const pricingUnavailable = localPrice.isLoading || !!localPrice.error ||
    (isAuthenticated && (authLoading || !user));
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(null); // `${slug}-stripe` or `${slug}-paypal`
  const [paypalEnabled, setPaypalEnabled] = useState(false);
  const [stripeEnabled, setStripeEnabled] = useState(false);
  const [bankTransferResult, setBankTransferResult] = useState(null);
  const [showBankTransferModal, setShowBankTransferModal] = useState(false);

  const rolePage = ROLE_PLAN_PAGES[user?.user_type] || null;

  useEffect(() => {
    fetchPlans();
    fetchGatewayStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_type]);

  const fetchPlans = async () => {
    try {
      const query = rolePage ? `?role=${rolePage.role}` : '';
      const res = await axios.get(`${API_URL}/api/subscriptions/plans${query}`);
      setPlans(res.data);
    } catch { toast.error('Failed to load plans'); }
    finally { setLoading(false); }
  };

  const fetchGatewayStatus = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/payments/gateways/public`);
      const gateways = res.data || [];
      setPaypalEnabled(gateways.find(g => g.gateway === 'paypal')?.is_enabled === true);
      setStripeEnabled(gateways.find(g => g.gateway === 'stripe')?.is_enabled === true);
    } catch {
      // fallback: no payment buttons shown if endpoint unavailable
    }
  };

  const handleStripeCheckout = async (plan) => {
    if (!isAuthenticated) { navigate('/login?redirect=/pricing'); return; }
    if (plan.price === 0) { toast.info('You are already on the Free plan'); return; }
    setCheckingOut(`${plan.slug}-stripe`);
    try {
      const res = await axios.post(`${API_URL}/api/payments/checkout`, {
        plan_slug: plan.slug,
        country: localPrice.country,
        origin_url: window.location.origin
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.url) window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start checkout');
    } finally { setCheckingOut(null); }
  };

  const handlePayPalCheckout = async (plan) => {
    if (!isAuthenticated) { navigate('/login?redirect=/pricing'); return; }
    if (plan.price === 0) { toast.info('You are already on the Free plan'); return; }
    setCheckingOut(`${plan.slug}-paypal`);
    try {
      const res = await axios.post(`${API_URL}/api/payments/paypal/create-order`, {
        plan_slug: plan.slug,
        country: localPrice.country,
        origin_url: window.location.origin
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.url) window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start PayPal checkout');
    } finally { setCheckingOut(null); }
  };

  const handleBankTransferCheckout = async (plan) => {
    if (!isAuthenticated) { navigate('/login?redirect=/pricing'); return; }
    if (plan.price === 0) { toast.info('You are already on the Free plan'); return; }
    setCheckingOut(`${plan.slug}-bank`);
    try {
      const res = await axios.post(`${API_URL}/api/payments/bank-transfer/create`, {
        plan_slug: plan.slug,
        country: localPrice.country,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setBankTransferResult({ ...res.data, planName: plan.name });
      setShowBankTransferModal(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create bank transfer request');
    } finally { setCheckingOut(null); }
  };

  return (
    <div className="min-h-screen bg-[#F0F4F8]" data-testid="pricing-page">
      <div className="bg-[#003366] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="bg-white/10 text-white border-white/20 mb-4">Pricing</Badge>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">{rolePage ? rolePage.title : 'Simple, transparent pricing'}</h1>
          <p className="text-white/80 text-lg max-w-2xl mx-auto">
            {rolePage ? rolePage.subtitle : "Choose the plan that's right for your recruitment needs. Upgrade or downgrade anytime."}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 pb-16">
        {localPrice.error && <p role="alert" className="mb-6 text-center text-sm text-red-600">{localPrice.error}</p>}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.map((plan) => {
            const Icon = iconForSlug(plan.slug);
            const isPopular = plan.is_popular;
            const isCurrentPlan = user?.subscription?.plan_slug === plan.slug;
            const stripeLoading = checkingOut === `${plan.slug}-stripe`;
            const paypalLoading = checkingOut === `${plan.slug}-paypal`;
            const bankLoading = checkingOut === `${plan.slug}-bank`;
            const anyLoading = stripeLoading || paypalLoading || bankLoading;

            return (
              <Card
                key={plan.id}
                className={`relative overflow-hidden transition-all hover:shadow-xl ${isPopular ? 'border-2 border-[#E11D48] shadow-lg scale-105' : ''}`}
                data-testid={`plan-card-${plan.slug}`}
              >
                {isPopular && (
                  <div className="absolute top-0 right-0 bg-[#E11D48] text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                    Most Popular
                  </div>
                )}
                <CardHeader className="text-center pb-2">
                  <div className={`w-14 h-14 mx-auto rounded-xl flex items-center justify-center mb-4 ${isPopular ? 'bg-[#E11D48]/10' : 'bg-[#003366]/10'}`}>
                    <Icon className={`w-7 h-7 ${isPopular ? 'text-[#E11D48]' : 'text-[#003366]'}`} />
                  </div>
                  <CardTitle className="text-xl">{plan.name}</CardTitle>
                  <CardDescription>{plan.description}</CardDescription>
                </CardHeader>
                <CardContent className="text-center">
                  <div className="mb-6">
                    <span className="text-4xl font-bold text-[#003366]">
                      {plan.price === 0 ? 'Free' : localPrice.format(plan.price)}
                    </span>
                    {plan.price > 0 && <span className="text-muted-foreground">/{plan.billing_cycle === 'annual' ? 'year' : 'month'} +VAT</span>}
                    {plan.price > 0 && localPrice.currency !== 'GBP' && !localPrice.error && (
                      <p className="text-xs text-muted-foreground mt-1">Charged in {localPrice.currency}</p>
                    )}
                  </div>

                  <ul className="space-y-3 mb-6 text-left">
                    {(plan.features || []).map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <CheckCircle2 className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isPopular ? 'text-[#E11D48]' : 'text-emerald-500'}`} />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {isCurrentPlan ? (
                    <Button disabled className="w-full h-11 bg-gray-200 text-gray-600">Current Plan</Button>
                  ) : plan.price === 0 ? (
                    <Button
                      onClick={() => isAuthenticated ? toast.info("You're already set up on the free tier — no action needed") : navigate('/register')}
                      className="w-full h-11 bg-[#003366] hover:bg-[#003366]/90"
                      data-testid={`subscribe-${plan.slug}`}
                    >
                      {isAuthenticated ? 'Included' : 'Get Started'}
                    </Button>
                  ) : (
                    <div className="space-y-2">
                      {stripeEnabled && (
                        <Button
                          onClick={() => handleStripeCheckout(plan)}
                          disabled={anyLoading || pricingUnavailable}
                          className={`w-full h-11 ${isPopular ? 'bg-[#E11D48] hover:bg-[#E11D48]/90' : 'bg-[#003366] hover:bg-[#003366]/90'}`}
                          data-testid={`subscribe-${plan.slug}`}
                        >
                          {stripeLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                          Pay with Card
                        </Button>
                      )}
                      {paypalEnabled && (
                        <Button
                          onClick={() => handlePayPalCheckout(plan)}
                          disabled={anyLoading || pricingUnavailable}
                          variant="outline"
                          className="w-full h-11 border-[#0070ba] text-[#0070ba] hover:bg-[#0070ba]/5"
                          data-testid={`paypal-${plan.slug}`}
                        >
                          {paypalLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                          Pay with PayPal
                        </Button>
                      )}
                      <Button
                        onClick={() => handleBankTransferCheckout(plan)}
                        disabled={anyLoading || pricingUnavailable}
                        variant="outline"
                        className="w-full h-11"
                        data-testid={`bank-transfer-${plan.slug}`}
                      >
                        {bankLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Landmark className="w-4 h-4 mr-2" />}
                        Pay by Bank Transfer
                      </Button>
                      {!stripeEnabled && !paypalEnabled && (
                        <p className="text-xs text-center text-muted-foreground">Card payments are temporarily unavailable — use bank transfer or contact support.</p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {showBankTransferModal && bankTransferResult && (
        <Dialog open={showBankTransferModal} onOpenChange={setShowBankTransferModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Bank Transfer Instructions</DialogTitle>
              <DialogDescription>Complete your transfer using the reference below. Your {bankTransferResult.planName} plan will be activated once we confirm receipt.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-gray-50 border">
                <p className="text-sm text-muted-foreground">Amount Due</p>
                <p className="text-2xl font-bold text-[#003366]">{formatCurrency(bankTransferResult.amount, bankTransferResult.currency)}</p>
              </div>
              <div className="p-4 rounded-lg bg-gray-50 border">
                <p className="text-sm text-muted-foreground">Payment Reference (include this in your transfer)</p>
                <p className="font-mono font-semibold text-lg">{bankTransferResult.reference}</p>
              </div>
              {bankTransferResult.instructions && (
                <div className="p-4 rounded-lg bg-blue-50 border border-blue-200 whitespace-pre-line text-sm text-blue-900">
                  {bankTransferResult.instructions}
                </div>
              )}
              <Button className="w-full" onClick={() => setShowBankTransferModal(false)}>Got it</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
